import { useState } from "react";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { Button, Modal, Spinner } from "react-bootstrap";
import Icon from "@mdi/react";
import { mdiChevronRight, mdiTrashCanOutline } from "@mdi/js";

import { useToast } from "../../context/ToastContext";
import { QuestionnaireResponseEntity } from "../../entities/QuestionnaireResponseEntity";
import QuestionnaireResponseService from "../../services/QuestionnaireResponseService";

import ListCard from "../ListCard";
import axios, { AxiosError } from "axios";

type EdificacaoPropsContent = {
	data: QuestionnaireResponseEntity;
	skeleton?: never;
};

type EdificacaoPropsSkeleton = {
	data?: never;
	onClick?: never;
	onRemove?: never;
	skeleton: true;
};

type EdificacaoProps = EdificacaoPropsContent | EdificacaoPropsSkeleton;

const toastTitle = "Edificação";

export default function Edificacao(props: EdificacaoProps) {
	const questionnaireResponseService = new QuestionnaireResponseService();

	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { handleToast } = useToast();

	const [removeModal, setRemoveModal] = useState(false);
	const [removeLoadingModal, setRemoveLoadingModal] = useState(false);
	const [removed, setRemoved] = useState(false);

	function handleClick() {
		if (!props.skeleton && props.data) {
			navigate("/avaliacaoDetalhes/" + props.data.id);
		}
	}

	function handleClickRemove(e: any) {
		e.preventDefault();
		e.stopPropagation();
		setRemoveModal(true);
	}

	async function handleRemove() {
		if (!props.skeleton && props.data) {
			setRemoveLoadingModal(true);
			try {
				let resp = await questionnaireResponseService.delete(props.data.id!);
				if (resp.status === 200) {
					queryClient.refetchQueries("avaliacoesRespostas");
					queryClient.invalidateQueries(["avaliacaoResposta", props.data.id]);
					setRemoved(true);
				} else {
					handleToast(toastTitle, resp.statusText, 5000, "danger");
				}
			} catch (error: any) {
				if (axios.isAxiosError(error)) {
					const axiosError = error as AxiosError<any>;
					handleToast(toastTitle, axiosError.response?.data.message, 5000, "danger");
				}
			}
			setRemoveModal(false);
			setRemoveLoadingModal(false);
		}
	}

	function handleCloseModal() {
		if (!removeLoadingModal) {
			setRemoveModal(false);
		}
	}

	let skeletonButton = "";
	if (props.skeleton) {
		skeletonButton = "skeleton skeleton-button";
	}

	if (removed) {
		return <></>;
	}

	return (
		<>
			<ListCard onClick={handleClick}>
				<ListCard.Content>
					<ListCard.Item auto>
						<ListCard.Title skeleton={props.skeleton}>Identificação</ListCard.Title>
						<ListCard.Text skeleton={props.skeleton}>#{props.data?.id ?? "..."}</ListCard.Text>
					</ListCard.Item>
					<ListCard.Item>
						<ListCard.Title skeleton={props.skeleton}>Avaliação</ListCard.Title>
						<ListCard.Text skeleton={props.skeleton}>
							{props.data?.questionnaireName ?? "..."} <span className="fw-lighter">#{props.data?.questionnaireId}</span>
						</ListCard.Text>
					</ListCard.Item>
					<ListCard.Item>
						<ListCard.Title skeleton={props.skeleton}>Usuário</ListCard.Title>
						<ListCard.Text skeleton={props.skeleton}>{props.data?.person?.name ?? "..."}</ListCard.Text>
					</ListCard.Item>
					<ListCard.Item auto>
						<ListCard.Title skeleton={props.skeleton}>Data</ListCard.Title>
						<ListCard.Text skeleton={props.skeleton}>{props.data?.responseDate ?? "..."}</ListCard.Text>
					</ListCard.Item>
				</ListCard.Content>
				<ListCard.Action>
					<Button variant="light" className={`me-2 ${skeletonButton}`} onClick={handleClickRemove}>
						<Icon path={mdiTrashCanOutline} size={1} className="text-danger" />
					</Button>
					<Icon path={mdiChevronRight} size={1} className={`text-muted m-2 ${skeletonButton}`} />
				</ListCard.Action>
			</ListCard>

			<Modal show={removeModal} onHide={handleCloseModal} centered>
				<Modal.Header closeButton>
					<Modal.Title>Remover Avaliação</Modal.Title>
				</Modal.Header>
				<Modal.Body className="text-center">{!removeLoadingModal ? "Deseja realmente remover esta avaliação?" : <Spinner />}</Modal.Body>
				{!removeLoadingModal && (
					<Modal.Footer>
						<Button variant="secondary" onClick={handleCloseModal}>
							Cancelar
						</Button>
						<Button variant="danger" onClick={handleRemove}>
							Remover
						</Button>
					</Modal.Footer>
				)}
			</Modal>
		</>
	);
}
